import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { useContextSelector } from 'use-context-selector';
import { ModalConfirmClose } from '../components/ModalConfirmClose';

import Sidebar from '../components/Sidebar';
import { ModalsProvider } from '../contexts/ModalsContext';
import { ModuleContext } from '../contexts/ModuleContext';
import { ExternalPatient, PatientContext, PatientProps } from '../contexts/PatientContext';
import { EligibilityPlanProps, PaymentContext } from '../contexts/PaymentContext';
import { PlatformContext } from '../contexts/PlatformContext';
import { RouterContext } from '../contexts/RouterContext';
import { UnitInfoProps } from '../models/UnitInfo';
import { BusnessRule, lambdaValidateApi, teleorientationApi } from '../services/api';
import { setCommonHeader } from '../services/headers';
import { IMainRouterContext, MainRouterContext, Page, Pages } from './pageComponents';
import useOnCloseSchedule from '../hooks/useOnCloseSchedule';
import Header from '../components/Header';
import { PAGES } from './pages';
import { removeMask } from '../utils/masks';
import { ServiceContext } from '../contexts/ServiceContext';
import { usePatientExternal } from '../store/patientExternal';
import { Appointments } from '../modules/appointmentBook';
import { useOpenSpecialist } from '../hooks/useOpenSpecialist';
import { FirebaseApp, FirebaseOptions } from 'firebase/app';
import { useInitializeFirebase } from '../hooks/useFirebase';

type TemplateProps = {
  withoutPaddingHeader?: boolean;
};

const Template = styled.div<TemplateProps>`
  ${({ withoutPaddingHeader }) => css`
    max-height: 100vh;
    padding-top: ${withoutPaddingHeader ? '0' : '72px'};

    @media (min-width: 900px) {
      margin: 0 auto;
      padding-top: ${withoutPaddingHeader ? '0' : '72px'};
    }
  `}
`;

const BodyApplication = styled.main<TemplateProps>`
  ${({ withoutPaddingHeader }) => css`
    height: ${withoutPaddingHeader ? 'var(--height-full)' : 'var(--height-body-without-header)'};
  `}
`;

type RouterHistory = {
  index: number;
  page: Page;
  component: ReactNode;
  params: Record<string, unknown>;
  title: string;
};

export type ScheduleItemRouterProps = {
  visible?: boolean;
  routerTo?:
    | 'PRODUCT'
    | 'SERVICE'
    | 'CONSULTAPPOINTMENT'
    | 'UNIT'
    | 'SPECIALIST'
    | 'CARE_PLUS'
    | 'CARE_PLUS_RESCHEDULE';
  patient?: ExternalPatient;
  onClose?: () => void;
  unit?: UnitInfoProps;
  authToken?: string;
  eligibility?: EligibilityPlanProps;
  patientToken?: string;
  fixed?: boolean;
  sdkAuthorization?: string;
  onCloseSDK?: () => void;
  selectedServiceId?: string;
  firstAppointment?: boolean;
  scheduleData?: Appointments;
  specialistNameToOpen?: string;
  firebaseOptions?: FirebaseOptions;
};

export const ScheduleItem: React.FC<ScheduleItemRouterProps> = ({
  visible,
  routerTo,
  patient,
  onClose,
  unit,
  authToken,
  patientToken,
  eligibility,
  fixed,
  sdkAuthorization,
  selectedServiceId,
  firstAppointment,
  scheduleData,
  specialistNameToOpen,
  firebaseOptions
}) => {
  const [historyList, setHistoryList] = React.useState<RouterHistory[]>([]);
  const [lastIndex, setLastIndex] = React.useState(0);

  const { currentStep, totalSteps } = useContextSelector(RouterContext, state => state);
  const { setEligibilityPlans } = useContextSelector(PaymentContext, state => state);
  const {
    isSchedulerOpen,
    setIsSchedulerOpen,
    isModalCloseOpen,
    setModalCloseOpen,
    setSelectedUnit,
    setFixedModule,
    setIsSDKAllowed
  } = useContextSelector(PlatformContext, state => state);

  const { selectItemToSchedule, setLogged, setSdkToken } = useContextSelector(ModuleContext, state => state);

  const { addPatientList, clearPatientList, setPatientSelected } = useContextSelector(PatientContext, state => state);

  const { setPatient: saveExternalPatient } = usePatientExternal();

  const { setSelectedServiceId } = useContextSelector(ServiceContext, state => state);

  const { openSpecialist } = useOpenSpecialist();

  const addHistoryList = ({ page, params, component }: Omit<RouterHistory, 'index'>) => {
    setHistoryList(list => [
      ...list,
      {
        page,
        params,
        component,
        index: lastIndex + 1,
        title: Pages[page].title
      }
    ]);
  };

  const addHistory = ({ page, params }: Omit<RouterHistory, 'index' | 'component' | 'title'>) => {
    const PageComponent = Pages[page].component;
    const title = Pages[page].title;
    const index = lastIndex + 1;
    const component = <PageComponent key={`${page}-${index}`} {...params} />;
    addHistoryList({ page, component, params, title });
    setLastIndex(index);
  };

  const navigate = (page: Page, params = {} as Record<string, unknown>) => {
    addHistory({ page, params });
  };

  const goBack = () => {
    setHistoryList(historyList.slice(0, -1));
    setLastIndex(lastIndex - 1);
  };

  const routerContextValue: IMainRouterContext = {
    navigate,
    goBack,
    history: historyList
  };

  const currentPage = historyList.find(({ index, component }) => {
    if (index === lastIndex) return component;
  });

  const moduleRouter: { [index: string]: () => void } = {
    PRODUCT: () => {
      selectItemToSchedule('PRODUCT_ENV');
      return navigate(Page.GENERAL, { routeTo: PAGES.RESOURCE_DETAILS.id });
    },
    SERVICE: () => {
      selectItemToSchedule('SERVICE_ENV');
      return navigate(Page.GENERAL, { routeTo: PAGES.RESOURCE_DETAILS.id });
    },
    SPECIALIST: () => {
      return navigate(Page.GENERAL, { routeTo: PAGES.SPECIALIST_LIST.id });
    },
    SPECIALIST_DETAILS: () => {
      return navigate(Page.GENERAL, { routeTo: PAGES.SPECIALIST_DETAILS.id });
    },
    UNIT: () => {
      setSelectedUnit(unit);
      return navigate(Page.UNIT_AUXILIARY_INFO);
    },
    CONSULTAPPOINTMENT: () => {
      return navigate(Page.CONSULT_APPOINTMENT);
    },
    CARE_PLUS: () => {
      return navigate(Page.CARE_PLUS, {
        sdkAuthorization,
        isFirstAppointment: !!firstAppointment
      });
    },
    CARE_PLUS_RESCHEDULE: () => {
      return navigate(Page.CARE_PLUS, { sdkAuthorization, scheduleData });
    },
    DEFAULT: () => {
      return navigate(Page.SERVICE_LIST, { isSDKAllowed: !!sdkAuthorization });
    }
  };

  const handlePatientProps = () => {
    if (patient?.idPatientExt) {
      saveExternalPatient({ ...patient, document: patient.document || '00000000000', documentType: 'CPF' });
      setLogged(true);
    }

    if (patient?.document && !patient?.idPatientExt) {
      patient.document = removeMask(patient.document);
      addPatientList(patient as PatientProps);
      setPatientSelected(patient as PatientProps);
    } else {
      clearPatientList();
    }

    if (patient && authToken) setLogged(true);
  };

  React.useEffect(() => {
    if (visible) {
      moduleRouter[routerTo || 'DEFAULT']();

      setIsSchedulerOpen(true);
    }
    if (authToken && patientToken) {
      setCommonHeader(teleorientationApi, 'x-access-token', `Bearer ${authToken}`);
      setCommonHeader(teleorientationApi, 'x-access-patient-token', `Bearer ${patientToken}`);
      setCommonHeader(lambdaValidateApi, 'x-access-token', `Bearer ${authToken}`);
      setCommonHeader(lambdaValidateApi, 'x-access-patient-token', `Bearer ${patientToken}`);
    }

    handlePatientProps();
  }, [visible]);

  React.useEffect(() => {
    if (eligibility) setEligibilityPlans([eligibility]);
  }, [eligibility]);

  React.useEffect(() => {
    if (isSchedulerOpen === false) confirmReset();
  }, [isSchedulerOpen]);

  React.useEffect(() => {
    if (selectedServiceId) setSelectedServiceId(selectedServiceId);
  }, [selectedServiceId]);

  React.useEffect(() => {
    if (specialistNameToOpen?.length) {
      openSpecialist(specialistNameToOpen);

      moduleRouter['SPECIALIST_DETAILS']();

      setIsSchedulerOpen(true);
      setFixedModule(true);
    }

    handlePatientProps();
  }, [specialistNameToOpen]);

  React.useEffect(() => {
    if (fixed) {
      moduleRouter[routerTo || 'DEFAULT']();
      setIsSchedulerOpen(true);
      setFixedModule(true);
    }

    if (sdkAuthorization?.length) {
      setCommonHeader(BusnessRule, 'Authorization', `Bearer ${sdkAuthorization}`);
      setIsSDKAllowed(true);
      setSdkToken(sdkAuthorization);
    }

    handlePatientProps();
  }, []);

  useInitializeFirebase({ firebaseOptions });

  const cancelCloseModal = () => setModalCloseOpen && setModalCloseOpen(false);

  const pageWithHeader = currentPage?.page === Page.SERVICE_LIST;

  const isSpecialPage =
    currentPage?.page === Page.APPOINTMENT_DETAILS ||
    currentPage?.page === Page.CONSULT_APPOINTMENT ||
    currentPage?.page === Page.UNIT_AUXILIARY_INFO;

  const { onCloseSchedule, confirmReset } = useOnCloseSchedule({
    isSpecialPage,
    onClose,
    onReset: () => setHistoryList([])
  });

  return (
    <MainRouterContext.Provider value={routerContextValue}>
      <Sidebar onClose={onCloseSchedule} visible={fixed || isSchedulerOpen}>
        <ModalsProvider>
          <Template withoutPaddingHeader={!pageWithHeader}>
            <BodyApplication withoutPaddingHeader={!pageWithHeader}>
              {pageWithHeader && (
                <Header
                  onClose={onCloseSchedule}
                  title={currentPage.title}
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                />
              )}
              {currentPage?.component}
              <ModalConfirmClose
                visible={!!isModalCloseOpen}
                onConfirmClose={confirmReset}
                onCancelClose={cancelCloseModal}
              />
            </BodyApplication>
          </Template>
        </ModalsProvider>
      </Sidebar>
    </MainRouterContext.Provider>
  );
};
